import { useEffect, useRef, useState } from 'react';

import Console from 'utility/Debug/Console';

export interface IntersectionObserverOptions extends IntersectionObserverInit {
    triggerOnce?: boolean;
    checkVisibilityOnFastScroll?: boolean;
    disconnectAfterVisible?: boolean;
}

const defaultOptions: IntersectionObserverOptions = {
    checkVisibilityOnFastScroll: false,
    disconnectAfterVisible: false,
};

/**
 * entry.isIntersecting needs fallback because it is null on some versions of MS Edge, and
 * entry.intersectionRatio is not enough alone because it could be 0 on some intersecting elements
 *
 * @see https://github.com/verlok/lazyload/blob/10.19.0/src/lazyload.intersectionObserver.js
 * @param entry {IntersectionObserverEntry}
 */
const isIntersecting = (entry: IntersectionObserverEntry) =>
    entry.isIntersecting || entry.intersectionRatio > 0;

const isElementInViewport = function (element: Element) {
    try {
        if (!element.isConnected) {
            return false;
        }

        const elRect = element.getBoundingClientRect();
        return (
            elRect.bottom >= 0 &&
            elRect.right >= 0 &&
            elRect.top <=
                (window.innerHeight || document.documentElement.clientHeight) &&
            elRect.left <=
                (window.innerWidth || document.documentElement.clientWidth)
        );
    } catch (error) {
        Console.error(error);
        return false;
    }
};

/**
 * Note: Currently does not support delay
 *
 * @param callback {() => void}
 * @param createOptions
 * @param onViewportLeft {() => void}
 * @param onViewportOutside {() => void}
 */
export const useIntersectionObserver = <H extends Element>(
    createOptions: IntersectionObserverOptions = defaultOptions,
    callback: () => void,
    onViewportLeft?: () => void,
    onViewportOutside?: () => void
): ((ref: H | null) => void) => {
    // the reference to the actual DOM-element
    const [ref, setRef] = useState<Element | null>(null);
    const [triggered, setTriggered] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const options = { ...defaultOptions, ...createOptions };
        // can trigger if `triggered` is `false`
        // will always trigger if `options.triggerOnce` is `false`
        const canTrigger = !options.triggerOnce || !triggered;
        if (!ref || !canTrigger) {
            return;
        }

        const onVisible = (): void => {
            setTriggered(true);
            callback();
            if (options.disconnectAfterVisible) {
                observer.disconnect();
            }
        };

        if (!triggered && options.checkVisibilityOnFastScroll) {
            const checkVisibility = () => {
                if (!triggered) {
                    timeoutRef.current = setTimeout(() => {
                        requestAnimationFrame(() => {
                            if (isElementInViewport(ref)) {
                                //https://stackoverflow.com/questions/61951380/intersection-observer-fails-sometimes-when-i-scroll-fast
                                Console.info(
                                    'normal IntersectionObserver doesnt hit, visible after timeoutcheck:',
                                    ref
                                );
                                onVisible();
                            } else {
                                checkVisibility();
                            }
                        });
                    }, 1000);
                }
            };
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            checkVisibility();
        }

        // we create a local `IntersectionObserver`
        // which gets garbage collected if it is not used anymore
        const observer = new IntersectionObserver(([entry]) => {
            if (entry) {
                if (isIntersecting(entry)) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    onVisible();
                } else {
                    if (!!onViewportLeft) {
                        const clientRect = ref.getBoundingClientRect();
                        if (clientRect.top <= 0) {
                            onViewportLeft();
                        }
                    }
                    // is not in viewport at the moment
                    if (!!onViewportOutside) {
                        onViewportOutside();
                    }
                }
            }
        }, options);
        if (ref) {
            // if we have a reference to a DOM-Element, we observe it
            observer.observe(ref);
        }
        return () => {
            // safe cleanup of `useEffect` re-run
            observer.disconnect();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [
        // React will re-run the effect and create a new `IntersectionObserver`
        // each time the following values change
        ref,
        createOptions.root,
        createOptions.rootMargin,
        createOptions.threshold,
        createOptions.triggerOnce,
        triggered,
        setTriggered,
        callback,
        onViewportLeft,
        onViewportOutside,
        createOptions,
    ]);

    return setRef;
};
